package co.maytes.standupbot.dashboard.pages.app.standups.sections

import androidx.compose.runtime.Composable
import co.maytes.standupbot.dashboard.components.widgets.Badge
import co.maytes.standupbot.dashboard.components.widgets.BadgeTone
import co.maytes.standupbot.dashboard.components.widgets.Button
import co.maytes.standupbot.dashboard.components.widgets.ButtonVariant
import co.maytes.standupbot.dashboard.components.widgets.cssRgb
import co.maytes.standupbot.dashboard.components.widgets.icons.LucideClock
import co.maytes.standupbot.dashboard.components.widgets.icons.LucideMessagesSquare
import co.maytes.standupbot.dashboard.features.standups.humanizeCron
import co.maytes.standupbot.dashboard.features.standups.shortTimezone
import co.maytes.standupbot.dashboard.styles.Palette
import co.maytes.standupbot.dashboard.styles.Spacing
import co.maytes.standupbot.dashboard.styles.Typography
import co.maytes.standupbot.shared.dto.standup.StandupDto
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontFamily
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.attributes.alt
import org.jetbrains.compose.web.dom.Img

@Composable
internal fun TeamHeader(
    standup: StandupDto,
    triggerInFlight: Boolean,
    onTrigger: () -> Unit,
    onRunForDate: () -> Unit,
    onDelete: () -> Unit,
) {
    Row(
        modifier = Modifier.fillMaxWidth().styleModifier {
            property("gap", "16px")
            property("flex-wrap", "wrap")
        },
        horizontalArrangement = Arrangement.SpaceBetween,
        verticalAlignment = Alignment.Top,
    ) {
        Column(modifier = Modifier.gap(Spacing.sm).styleModifier { property("min-width", "0") }) {
            Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(Spacing.sm)) {
                Box(
                    modifier = Modifier.styleModifier {
                        property("width", "32px")
                        property("height", "32px")
                        property("border-radius", "8px")
                        property("background-color", Palette.accentSubtle.cssRgb())
                        property("color", Palette.accent.cssRgb())
                        property("display", "inline-flex")
                        property("align-items", "center")
                        property("justify-content", "center")
                        property("flex-shrink", "0")
                    },
                ) { LucideMessagesSquare(size = 18) }
                SpanText(
                    text = standup.name,
                    modifier = Modifier
                        .fontSize(Typography.h1.size)
                        .fontWeight(Typography.h1.weight)
                        .color(Palette.textPrimary),
                )
                Badge(
                    label = if (standup.enabled) "Active" else "Paused",
                    tone = if (standup.enabled) BadgeTone.Success else BadgeTone.Neutral,
                    leadingDot = if (standup.enabled) Palette.success else Palette.textTertiary,
                )
            }
            Row(
                modifier = Modifier.styleModifier {
                    property("gap", "10px")
                    property("flex-wrap", "wrap")
                    property("align-items", "center")
                },
            ) {
                SlackChannelChip(
                    text = standup.slackChannelName ?: standup.slackChannelId,
                    monoText = standup.slackChannelName == null,
                )
                Row(
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(Spacing.xxs),
                ) {
                    LucideClock(size = 14, strokeColor = Palette.textTertiary.cssRgb())
                    SpanText(
                        text = "${humanizeCron(standup.scheduleCron)} · ${shortTimezone(standup.timezone)}",
                        modifier = Modifier
                            .fontSize(Typography.bodySmall.size)
                            .color(Palette.textSecondary),
                    )
                }
            }
        }
        Row(horizontalArrangement = Arrangement.spacedBy(Spacing.sm), verticalAlignment = Alignment.CenterVertically) {
            Button(
                label = if (triggerInFlight) "Running…" else "Run now",
                variant = ButtonVariant.Primary,
                enabled = !triggerInFlight,
                onClick = onTrigger,
            )
            Button(
                label = "Run for date…",
                variant = ButtonVariant.Ghost,
                enabled = !triggerInFlight,
                onClick = onRunForDate,
            )
            Button(
                label = "Delete",
                variant = ButtonVariant.Ghost,
                modifier = Modifier.color(Palette.error),
                onClick = onDelete,
            )
        }
    }
}

@Composable
private fun SlackChannelChip(text: String, monoText: Boolean) {
    Row(
        modifier = Modifier.styleModifier {
            property("gap", "6px")
            property("align-items", "center")
            property("background-color", Palette.surfaceSubtle.cssRgb())
            property("border-radius", "999px")
            property("padding", "4px 10px")
            property("min-width", "0")
        },
    ) {
        Img(
            src = "/brand/slack.svg",
            attrs = Modifier
                .styleModifier {
                    property("width", "14px")
                    property("height", "14px")
                    property("flex-shrink", "0")
                }
                .toAttrs { alt("Slack") },
        )
        SpanText(
            text = "#",
            modifier = Modifier
                .fontSize(Typography.caption.size)
                .fontWeight(600)
                .color(Palette.textTertiary),
        )
        SpanText(
            text = text,
            modifier = Modifier
                .fontSize(Typography.bodySmall.size)
                .fontWeight(500)
                .color(Palette.textSecondary)
                .let { if (monoText) it.fontFamily(*Typography.MONO_STACK) else it }
                .styleModifier {
                    property("overflow", "hidden")
                    property("text-overflow", "ellipsis")
                    property("white-space", "nowrap")
                },
        )
    }
}
